import { ConditionalFilter } from './filters/conditional-filter';
import { EqualFilter } from './filters/equal-filter';
import { UnableToParseValue } from './exceptions';
import { Filter } from './filter';
import { ErrorType, FieldCondition, FieldValues } from './types';
import { ValidationResult } from './validation-result';

export abstract class FieldValidator<OutputType> {
    protected filters: Array<Filter<OutputType>> = [];
    private isRequired = true;
    protected validationConditions: Array<FieldCondition> = [];

    protected constructor(public readonly stringType: string) {}

    async validate(fieldName: string, allFields: FieldValues): Promise<ValidationResult> {
        const validationResult = new ValidationResult();
        const fieldValue = allFields[fieldName];

        // If the field should not be validated, we stop here
        if (!this.shouldBeValidated(allFields)) {
            // We want to check that the given field has no value.
            // Instead, we return a validation error.
            // TODO: return 'invalidValue' error code and proper message to explain
            // this error is raised when a field that should not be validated has a value
            if (fieldValue !== undefined) {
                validationResult.pushErrors(fieldName, [
                    {
                        code: 'equal',
                        expectedValue: undefined,
                    },
                ]);
            }

            return validationResult;
        }

        // Else, we run type validation
        const typingErrors = await this.validateType(fieldValue, allFields);
        if (typingErrors.length > 0) {
            validationResult.pushErrors(fieldName, typingErrors);
            return validationResult;
        }

        // We parse a validated value, which is in the expected type OR which is undefined
        const parsedValue: OutputType | undefined = this.parse(fieldValue, allFields);

        // If parsed value is undefined, we will not run any filter (it's useless) and we return a
        // required error if the field should be there.
        if (parsedValue === undefined) {
            if (this.isRequired) {
                validationResult.pushErrors(fieldName, [{ code: 'required' }]);
            }
            return validationResult;
        }

        for (const filter of this.filters) {
            validationResult.pushErrors(fieldName, await filter.validate(parsedValue, allFields));
        }
        return validationResult;
    }

    private shouldBeValidated(fieldValues: FieldValues): boolean {
        for (const shouldBeValidated of this.validationConditions) {
            if (!shouldBeValidated(fieldValues)) {
                return false;
            }
        }
        return true;
    }

    private async validateType(
        fieldValue: unknown,
        allFields: FieldValues,
    ): Promise<Array<ErrorType>> {
        try {
            this.parse(fieldValue, allFields);
        } catch (error: any) {
            if (error instanceof UnableToParseValue) {
                return [
                    {
                        code: 'invalidType',
                        expectedType: this.stringType,
                    },
                ];
            }
            throw error;
        }
        return [];
    }

    conditionalFiltering(
        condition: FieldCondition,
        filtersIfTrue: Array<Filter<OutputType>>,
        filtersIfFalse: Array<Filter<OutputType>>,
    ): this {
        this.filters.push(new ConditionalFilter(condition, filtersIfTrue, filtersIfFalse));
        return this;
    }

    custom(filter: Filter<OutputType>): this {
        this.filters.push(filter);
        return this;
    }

    equal(expectedValue: OutputType): this {
        this.filters.push(new EqualFilter<OutputType>(expectedValue));
        return this;
    }

    if(validationCondition: FieldCondition): this {
        this.validationConditions.push(validationCondition);
        return this;
    }

    optional(): this {
        this.isRequired = false;
        return this;
    }

    // All parse methods are parsing optional values. Requirements are managed in this class using
    // filters: by default every field is required but can be switched optional by calling
    // optional() method.
    abstract parse(fieldValue: unknown, allFields: FieldValues): OutputType | undefined;
}
